import React, { useEffect, useState } from 'react'

import { useNavigate } from 'react-router'
import styled, { CSS } from 'styled-components'

import { IconEnum, Button } from '@atoms/index'
import { useConfig } from '@client/contexts/ConfigProvider'
import { PaddedSectionContainer, ResponsivePXValue, ServingSizeEnum, SidePagePadding, theme } from '@components/Theme'
import { useEvents } from '@contexts/GTMProvider'
import { useUserDetailsQuery, AllergenFragment, FrozenInstructionFragment, ServingSuggestionFragment, FrozenMealDishListFragment, FrozenMealListFragment, FrozenMealDishDetailsFragment } from '@hooks/api'
import { ItemInterfaceNew } from '@lib/GTM'
import { Utilities } from '@lib/Utilities'
import { SelectOption, TextListItem, TextListProps } from '@molecules/index'
import { CraftMealDishDetailsProps, CraftMealDishDetails, MoreAboutMeal, MoreAboutMealProps, CraftMealYouMightAlsoLike } from '@organisms/index'
import { ProductStockStatusEnum } from '@uctypes/api/globalTypes'

import { NewGA4Product } from '.'

const BackButtonContainer = styled.div`
 display: flex;
 ${ResponsivePXValue('margin-bottom', { mobile: '24px', tablet: '28px', desktop: '36px' })}
 ${SidePagePadding}
`
const GhostButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  ${ResponsivePXValue('padding-bottom', { mobile: '16px' })}
`
const SectionContainer = styled.div<{ $backgroundColor?: string, $customBackground?: string, $padded: boolean }>`
  ${(props): CSS => PaddedSectionContainer(props.$backgroundColor, props.$customBackground, props.$padded)};
`

enum TabEnum {
  ABOUT_THE_DISH = 'ABOUT_THE_DISH',
  INGREDIENTS = 'INGREDIENTS',
  COOKING_INSTRUCTIONS = 'COOKING_INSTRUCTIONS',
  SERVING_SUGGESTIONS = 'SERVING_SUGGESTIONS'
}

export interface CraftMealPDPProps {
  dish: FrozenMealDishDetailsFragment
  loading: boolean
}

interface CraftMealPDPState {
  activeTab: TabEnum
  loading: boolean
  disabled: boolean
  servingSize: string | null
  quantity: number
  displayDiscoveryTag: boolean

}

const DEFAULT_STATE: CraftMealPDPState = {
  activeTab: TabEnum.ABOUT_THE_DISH,
  loading: false,
  disabled: true,
  servingSize: null,
  quantity: 1,
  displayDiscoveryTag: false,

}

export function CraftMealPDP({ dish, loading }: CraftMealPDPProps): JSX.Element {

  const [state, setState] = useState<CraftMealPDPState>({ ...DEFAULT_STATE })
  const events = useEvents()
  const config = useConfig()

  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })
  const navigate = useNavigate()

  const dishMealsFilter = (meal: FrozenMealListFragment): boolean => dish?.products?.some(product => product.id === meal.id)

  const productList = dish?.products

  const dishMeals: (FrozenMealListFragment)[] = productList?.filter(dishMealsFilter)
  const servingSize = state?.servingSize
  const servingMeal = dishMeals?.find(meal => meal.id === servingSize)

  const allergens: string[] = []

  dish?.allergens?.forEach((allergen: AllergenFragment) => { allergens.push(allergen.title) })

  const finalAllergens = `${allergens.join(', ')}. Packed in a facility that handles all major food allergens and gluten, including Milk, Eggs, Fish,
                          Shellfish, Tree Nuts, Peanuts, Wheat, Soybeans.`

  useEffect(() => {
    if (!loading) {
      const logArr = [] as ItemInterfaceNew[]
      dish?.products?.forEach((meal, index) => {

        const logData = {
          itemName: dish?.name + ' - ' + meal?.frozenPortionSize,
          itemId: meal?.id,
          price: meal?.price,
          itemBrand: 'UCOOK',
          itemCategory: dish?.frozenCategories.map((cat) => cat.id).join(', '),
          itemVariant: dish?.frozenCategories.map((cat) => cat.title).join(', '),
          itemListName: 'Craft Meals',
          index,
          itemImage: dish?.coverImage?.location,
          itemStockCount: meal?.stockCount > 1 ? 1 : 0,
          itemChef: dish?.chef?.name,
          isMealkit: 'no',
        }
        const snakedData = Utilities.toSnakeCase(logData) as unknown as ItemInterfaceNew

        logArr.push(snakedData)
      })

      logArr.sort((a, b) => {
        if (a.item_name.includes(state?.servingSize)) return -1
        if (b.item_name.includes(state?.servingSize)) return 1
        return 0
      })

      events.hasViewedMultipleProducts('Craft Meals', logArr, userDetailsData?.currentUser?.id)
      events.hasSelectedMultipleItems('Craft Meals', logArr, userDetailsData?.currentUser?.id)
    }
  }, [loading, state.servingSize])

  const getServingSizeTitle = (product: FrozenMealListFragment): string => {
    let title = ServingSizeEnum[product.frozenPortionSize] as string

    if (product.stockStatus === ProductStockStatusEnum.OUT_OF_STOCK) {
      title = title + ' - Out of stock'
    }

    return title
  }

  const getServingSizeOptions = (): SelectOption[] => {
    const options: SelectOption[] = dish?.products?.map((product) => ({
      title: getServingSizeTitle(product),
      disabled: product.stockStatus === ProductStockStatusEnum.OUT_OF_STOCK,
      value: product.id,
    })) || []
    return options.sort((a, b) => a.title > b.title ? 1 : -1)
  }

  const getCookingInstructions = (meal: FrozenMealListFragment): TextListItem[] => {
    return meal?.frozenInstructions?.map((instruction: FrozenInstructionFragment) => ({
      title: instruction.title,
      subTitle: instruction?.description || null,
      image: instruction?.image || null,
    }))
  }

  const getServingSuggestions = (dish: FrozenMealDishListFragment): TextListItem[] => {
    return dish?.servingSuggestions?.map((servingSuggestion: ServingSuggestionFragment) => ({
      title: servingSuggestion.title,
      subTitle: servingSuggestion?.description || null,
      image: servingSuggestion?.image || null,
    }))
  }

  const servingSizeChangeHandler = (servingSize: string) => {
    setState({ ...state, servingSize })
  }

  const servingSizeOptions = getServingSizeOptions()

  const dishDetailsData: CraftMealDishDetailsProps = {
    dish,
    allergens: finalAllergens,
    servingSizeOptions,
    servingSize,
    servingSizeChange: servingSizeChangeHandler,
  }

  const cookingInstructions: TextListProps = {
    items: getCookingInstructions(servingMeal),
  }

  const servingSuggestions: TextListProps = {
    items: getServingSuggestions(dish),
  }

  const moreAboutMealData: MoreAboutMealProps = {
    chef: {
      name: dish?.chef?.name,
      about: dish?.story,
      image: dish?.chef.profileImage,
    },
    ingredients: {
      ingredients: dish?.ingredients,
      nutritionalInfo: {
        nutrition: dish?.nutrition,
      },
    },
    isPizza: false,
    cookingInstructions,
    servingSuggestions,
    servingSizeOptions,
    servingSize,
    servingSizeChange: servingSizeChangeHandler,
  }

  const _handleBackClicked = (): void => {

    if (config.isBrowser()) {
      const hasBoughtBefore = window.location.href.includes('bought-before')
      if (hasBoughtBefore) {
        navigate('/frozen/bought-before')
      }
    }
    navigate(-1)
  }

  const _getCraftMealServingSize = (): string => {
    if (config.isBrowser()) {
      return window.sessionStorage.getItem('craftMealServingSize')
    }
  }

  useEffect(() => {
    const sessionServingSize = _getCraftMealServingSize()
    let servingSize = servingSizeOptions[0]?.value?.toString()
    if (sessionServingSize) {
      servingSize = sessionServingSize
    }
    setState({ ...state, servingSize })
  }, [])

  return (
    <>
      <SectionContainer $padded={false}>
        <BackButtonContainer>
          <Button
            title='BACK'
            variant='secondary'
            displayBorder={false}
            color='grey'
            leftIcon={IconEnum.CHEVRON_BACK_OUTLINE}
            iconColor={theme.colors.oranges.coral}
            loading={loading}
            onClick={_handleBackClicked} />
        </BackButtonContainer>
        <CraftMealDishDetails {...dishDetailsData} />
      </SectionContainer>
      <SectionContainer $backgroundColor='grey' $padded>
        <MoreAboutMeal {...moreAboutMealData} />
      </SectionContainer>
      <SectionContainer $padded>
        <CraftMealYouMightAlsoLike dish={dish} />
        <GhostButtonContainer>
          <Button variant='secondary' title='BACK TO THE MENU' color='grey' onClick={_handleBackClicked} />
        </GhostButtonContainer>
      </SectionContainer>
    </>
  )
}
